import React from 'react';
import {
  LayoutDashboard,
  FileText,
  Search,
  GitCompare,
  AlertTriangle,
  BarChart3,
  MessageSquare,
  BookOpen,
  X,
  ChevronLeft,
} from 'lucide-react';

export interface NavItem {
  name: string;
  icon: React.ReactNode;
  href: string;
  current: boolean;
}

interface SidebarProps {
  isOpen: boolean;
  isMobile: boolean;
  onClose: () => void;
  onToggleCollapse?: () => void;
  isCollapsed?: boolean;
  currentPath: string;
  onNavigate: (path: string) => void;
}

const navigation: Omit<NavItem, 'current'>[] = [
  { name: 'Dashboard', icon: <LayoutDashboard className="h-5 w-5" />, href: '/' },
  { name: 'Contracts', icon: <FileText className="h-5 w-5" />, href: '/contracts' },
  { name: 'Regulations', icon: <BookOpen className="h-5 w-5" />, href: '/regulations' },
  { name: 'Chat', icon: <MessageSquare className="h-5 w-5" />, href: '/chat' },
  { name: 'Search', icon: <Search className="h-5 w-5" />, href: '/search' },
  { name: 'Analysis', icon: <GitCompare className="h-5 w-5" />, href: '/analysis' },
  { name: 'Risks', icon: <AlertTriangle className="h-5 w-5" />, href: '/risks' },
  { name: 'Reports', icon: <BarChart3 className="h-5 w-5" />, href: '/reports' },
];

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  isMobile,
  onClose,
  onToggleCollapse,
  isCollapsed = false,
  currentPath,
  onNavigate,
}) => {
  const sidebarClasses = isMobile
    ? `fixed inset-y-0 left-0 z-50 w-64 bg-secondary-800 transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`
    : `hidden md:flex md:flex-shrink-0 transition-all duration-300 ${
        isCollapsed ? 'md:w-16' : 'md:w-64'
      }`;

  const handleNavClick = (href: string) => {
    onNavigate(href);
    if (isMobile) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <div className={sidebarClasses} data-testid="sidebar">
        <div className="flex flex-col h-full bg-secondary-800">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-secondary-700">
            {!isCollapsed && (
              <span className="text-xl font-bold text-white truncate">
                Triple-S
              </span>
            )}
            {isMobile ? (
              <button
                onClick={onClose}
                className="p-2 rounded-md text-secondary-400 hover:text-white hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-white"
                data-testid="sidebar-close-button"
                aria-label="Close sidebar"
              >
                <X className="h-6 w-6" />
              </button>
            ) : (
              onToggleCollapse && (
                <button
                  onClick={onToggleCollapse}
                  className={`p-2 rounded-md text-secondary-400 hover:text-white hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-white transition-transform ${
                    isCollapsed ? 'rotate-180' : ''
                  }`}
                  data-testid="sidebar-collapse-button"
                  aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              )
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 overflow-y-auto" data-testid="sidebar-nav">
            <ul className="space-y-1 px-2">
              {navigation.map((item) => {
                const isCurrent = currentPath === item.href;
                return (
                  <li key={item.name}>
                    <button
                      onClick={() => handleNavClick(item.href)}
                      className={`w-full flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isCurrent
                          ? 'bg-primary-700 text-white'
                          : 'text-secondary-300 hover:bg-secondary-700 hover:text-white'
                      } ${isCollapsed && !isMobile ? 'justify-center' : ''}`}
                      data-testid={`nav-item-${item.name.toLowerCase()}`}
                      aria-current={isCurrent ? 'page' : undefined}
                    >
                      <span className="flex-shrink-0">{item.icon}</span>
                      {(!isCollapsed || isMobile) && (
                        <span className="ml-3">{item.name}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Sidebar Footer */}
          <div className="border-t border-secondary-700 p-4">
            {(!isCollapsed || isMobile) && (
              <div className="text-xs text-secondary-500">
                <p>Medicaid Contract</p>
                <p>Analysis Platform</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
